(() => {
  'use strict';
  function saleActive(p, now) {
    const start = Date.parse(p.starts), end = Date.parse(p.ends);
    return p.enabled === true && p.available === true && Number.isFinite(start) && Number.isFinite(end) && end > start && start <= now && now < end;
  }
  function priority(p, now) {
    return typeof p.priority === 'number' && Number.isSafeInteger(p.priority) && p.priority >= 1 && p.priority <= 999999 && p.available === true ? p.priority : Infinity;
  }
  function compareIds(a, b) {
    const x = String(a).replace(/^0+/, '') || '0', y = String(b).replace(/^0+/, '') || '0';
    return x.length - y.length || (x < y ? -1 : x > y ? 1 : 0);
  }
  function orderProducts(rows, now, applyPins = true) {
    const seen = new Set();
    return rows.filter(p => { if (seen.has(p.id)) return false; seen.add(p.id); return true; })
      .map((p, i) => ({ p, i, sale: saleActive(p, now), rank: applyPins ? priority(p, now) : Infinity }))
      .sort((a, b) => Number(b.sale) - Number(a.sale) || (a.rank !== b.rank ? (a.rank < b.rank ? -1 : 1) : a.rank !== Infinity ? compareIds(a.p.id, b.p.id) : a.i - b.i))
      .map(x => x.p);
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = { saleActive, priority, compareIds, orderProducts, categoryPinningAllowed };
  if (typeof window === 'undefined' || window.lisaPinningLoaded) return;
  window.lisaPinningLoaded = true;
  const controllers = new Map();
  let serverOffset = 0;
  const now = () => Date.now() + serverOffset;
  function requestUrl(path, section, page) {
    const url = new URL(path, location.origin);
    if (url.origin !== location.origin) throw new Error('Invalid product source');
    url.searchParams.delete('sections');
    url.searchParams.set('section_id', section);
    if (page !== undefined) url.searchParams.set('page', String(page));
    const preview = new URL(location.href).searchParams.get('preview_theme_id');
    if (preview) url.searchParams.set('preview_theme_id', preview);
    return url;
  }
  async function fetchDocument(url, signal) {
    const before = Date.now();
    const response = await fetch(url, { signal, credentials: 'same-origin', cache: 'no-store' });
    if (!response.ok) throw new Error('Storefront response ' + response.status);
    const date = Date.parse(response.headers.get('date'));
    if (Number.isFinite(date)) serverOffset = date - (before + Date.now()) / 2;
    return new DOMParser().parseFromString(await response.text(), 'text/html');
  }
  async function readCatalog(path, signal) {
    async function page(number) {
      const doc = await fetchDocument(requestUrl(path, 'lisa-pin-data', number), signal);
      const node = doc.querySelector('[data-lisa-pin-data]');
      if (!node) throw new Error('Pin data unavailable');
      const data = JSON.parse(node.textContent);
      if (data.page !== number || !Array.isArray(data.products)) throw new Error('Invalid pin data');
      return data;
    }
    const first = await page(1);
    if (first.total > 25000 || first.pages > 100) throw new Error('Catalog exceeds storefront pagination limit');
    const pages = [first];
    for (let n = 2; n <= first.pages; n += 4) {
      const batch = await Promise.all(Array.from({ length: Math.min(4, first.pages - n + 1) }, (_, i) => page(n + i)));
      if (batch.some(p => p.total !== first.total || p.pages !== first.pages)) throw new Error('Catalog changed while loading');
      pages.push(...batch);
    }
    const rows = pages.flatMap(p => p.products);
    if (rows.length !== first.total || new Set(rows.map(p => p.id)).size !== rows.length) throw new Error('Incomplete catalog');
    return rows;
  }
  class SaleLabel extends HTMLElement {
    connectedCallback() { this.update(); this.timer = setInterval(() => this.update(), 1000); }
    disconnectedCallback() { clearInterval(this.timer); }
    update() {
      if (!this.product) {
        const data = this.querySelector('[data-lisa-sale-product]');
        try { this.product = JSON.parse(data?.textContent || 'null'); } catch (_) {}
      }
      const time = now();
      let product = this.product;
      if (product && this.dataset.selectedVariant) {
        const variants = product.variants.filter(variant => String(variant.id) === this.dataset.selectedVariant);
        product = { ...product, variants, variantCount: variants.length, available: variants.some(variant => variant.available) };
      }
      const sale = product && window.LisaSales?.offer(product, time);
      this.hidden = !sale;
      const regular = this.parentElement?.querySelector('[data-lisa-regular-price]');
      if (regular) regular.hidden = Boolean(sale);
      if (!sale) return;
      const formatter = new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency: this.dataset.currency || 'USD' });
      const money = cents => formatter.format(cents / 100);
      const seconds = Math.max(0, Math.ceil((Date.parse(sale.ends) - time) / 1000));
      this.querySelector('[data-countdown]').textContent = [Math.floor(seconds / 3600), Math.floor(seconds % 3600 / 60), seconds % 60].map(n => String(n).padStart(2, '0')).join(':');
      const signature = JSON.stringify(sale);
      if (signature === this.signature) return;
      this.signature = signature;
      this.querySelector('[data-regular]').textContent = money(sale.price);
      const discounted = this.querySelector('[data-discounted]');
      discounted.classList.add('lisa-price-amount');
      discounted.replaceChildren();
      if (sale.from) discounted.append(document.createTextNode(this.dataset.from + ' '));
      const sellingPrice = sale.salePrice;
      formatter.formatToParts(sellingPrice / 100).forEach(part => {
        if (part.type === 'integer' || part.type === 'group') {
          const whole = document.createElement('span');
          whole.className = 'lisa-price-whole';
          whole.textContent = part.value;
          discounted.append(whole);
        } else {
          discounted.append(document.createTextNode(part.value));
        }
      });
      this.querySelector('[data-unit]').textContent = sale.pack ? ' · ' + sale.packText + sale.pluralText : '';
      this.querySelector('[data-pack]').textContent = sale.pack ? money(Math.round(sale.salePrice / sale.quantity)) + ' / ' + sale.unitText : '';
    }
  }
  if (!customElements.get('lisa-flash-sale')) customElements.define('lisa-flash-sale', SaleLabel);
  class PinGrid {
    constructor(grid) {
      this.grid = grid;
      this.abort = new AbortController();
      this.cards = new Map();
      this.rows = null;
      this.busy = false;
      this.lastFetch = 0;
      this.retryAfter = 0;
      this.retryFetchAfter = 0;
      for (const li of grid.children) {
        const card = li.querySelector('[data-lisa-product-id]');
        if (card) this.cards.set(card.dataset.lisaProductId, li);
      }
      this.home = grid.dataset.lisaPinGrid === 'home';
      this.usePins = categoryPinningAllowed(grid, location.pathname);
      this.path = this.home ? grid.dataset.catalogUrl : location.pathname + location.search;
      this.page = this.home ? 1 : Number(grid.dataset.page || 1);
      this.limit = Number(grid.dataset.pageSize);
      this.initialIds = [...this.cards.keys()];
      this.tick();
    }
    destroy() { this.abort.abort(); }
    async getCard(product) {
      if (this.cards.has(product.id)) return this.cards.get(product.id);
      const doc = await fetchDocument(requestUrl(product.url, 'lisa-pin-card'), this.abort.signal);
      const wrapper = doc.querySelector('[data-lisa-fetched-card]');
      if (!wrapper || wrapper.dataset.lisaFetchedCard !== product.id) throw new Error('Product card unavailable');
      const li = document.createElement('li');
      li.className = this.home ? 'merchandise-first__item' : 'grid__item';
      li.append(...wrapper.childNodes);
      if (!this.home && this.grid.dataset.imageRatio !== 'square') {
        const img = li.querySelector('.card__media img');
        const ratio = this.grid.dataset.imageRatio === 'portrait' ? 0.8 : (Number(img?.getAttribute('width')) / Number(img?.getAttribute('height')) || 1);
        for (const el of li.querySelectorAll('[style*="--ratio-percent"]')) el.style.setProperty('--ratio-percent', (100 / ratio) + '%');
      }
      {
        const action = this.grid.querySelector('.merchandise-first__action');
        if (action) {
          const link = action.cloneNode(true);
          link.href = product.url;
          link.classList.remove('lisa-cart-confirmed');
          link.removeAttribute('aria-busy');
          link.querySelectorAll('.lisa-cart-check, .lisa-button-cart-count, .lisa-button-cart-count-label').forEach(node => node.remove());
          const title = li.querySelector('.card__heading a')?.textContent.trim() || '';
          const hidden = link.querySelector('.visually-hidden');
          if (hidden) hidden.textContent = ': ' + title;
          li.append(link);
        }
      }
      this.cards.set(product.id, li);
      return li;
    }
    async tick(force = false) {
      if (this.busy || !this.grid.isConnected || (!force && Date.now() < this.retryAfter)) return;
      if (!this.home) {
        const selected = new URLSearchParams(location.search).get('sort_by');
        if (selected && selected !== 'manual') return;
      }
      this.busy = true;
      try {
        if (!this.rows || force || (Date.now() - this.lastFetch > 300000 && Date.now() >= this.retryFetchAfter)) {
          let rows, refreshed = false;
          try {
            rows = await readCatalog(this.path, this.abort.signal);
            refreshed = true;
          } catch (error) {
            if (!this.rows || error.name === 'AbortError') throw error;
            this.retryFetchAfter = Date.now() + 30000;
            rows = this.rows;
          }
          if (this.rows && rows !== this.rows) {
            const previous = new Map(this.rows.map(p => [p.id, p]));
            for (const product of rows) {
              const old = previous.get(product.id);
              if (old && JSON.stringify(old) !== JSON.stringify(product)) this.cards.delete(product.id);
            }
          }
          if (this.home) {
            const featured = (this.grid.dataset.featuredIds || '').split(',').filter(Boolean);
            const index = new Map(featured.map((id, i) => [id, i]));
            rows = rows.map((p, i) => ({ p, i })).sort((a, b) => (index.get(a.p.id) ?? Infinity) - (index.get(b.p.id) ?? Infinity) || a.i - b.i).map(x => x.p);
          }
          this.rows = rows;
          if (refreshed) this.lastFetch = Date.now();
        }
        const ranked = this.rows.map(product => {
          if (!window.LisaSales) return product;
          const sale = window.LisaSales.offer(product, now());
          return { ...product, enabled: Boolean(sale), starts: sale?.starts, ends: sale?.ends };
        });
        const sorted = orderProducts(ranked, now(), this.usePins);
        for (const product of this.rows) {
          const label = this.cards.get(product.id)?.querySelector('lisa-flash-sale');
          if (label) {
            label.product = product;
            label.update();
          }
        }
        const desired = sorted.slice((this.page - 1) * this.limit, this.page * this.limit);
        const ids = desired.map(p => p.id).join(',');
        const current = [...this.grid.children].map(li => li.querySelector('[data-lisa-product-id]')?.dataset.lisaProductId).join(',');
        if ((ids === current && desired.every(p => this.cards.has(p.id))) || !desired.length) return;
        const nodes = [];
        for (let i = 0; i < desired.length; i += 4) nodes.push(...await Promise.all(desired.slice(i, i + 4).map(p => this.getCard(p))));
        if (this.abort.signal.aborted || !this.grid.isConnected || this.grid.contains(document.activeElement)) return;
        this.grid.replaceChildren(...nodes);
      } catch (error) {
        this.retryAfter = Date.now() + 30000;
        if (error.name !== 'AbortError') console.warn('Pinned cards: keeping the current product grid.', error.message);
      } finally { this.busy = false; }
    }
  }
  function categoryPinningAllowed(grid, pathname) {
    const match = pathname.match(/\/collections\/([^/]+)(?:\/|$)/);
    return grid.dataset.lisaPinGrid === 'collection' && Boolean(match) && !['all', 'frontpage', 'featured-product'].includes(match[1]);
  }
  function scan() {
    for (const [grid, controller] of controllers) if (!grid.isConnected) { controller.destroy(); controllers.delete(grid); }
    for (const grid of document.querySelectorAll('[data-lisa-pin-grid]')) if (!controllers.has(grid)) controllers.set(grid, new PinGrid(grid));
  }
  let queued = false;
  const observer = new MutationObserver(() => {
    if (queued) return;
    queued = true;
    queueMicrotask(() => { queued = false; scan(); });
  });
  function init() { scan(); observer.observe(document.body, { childList: true, subtree: true }); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true }); else init();
  setInterval(() => { if (!document.hidden) for (const controller of controllers.values()) controller.tick(); }, 1000);
  document.addEventListener('visibilitychange', () => { if (!document.hidden) for (const controller of controllers.values()) controller.tick(true); });
})();





